/**
 * Procedure rows for the Forms → "Procedure Forms & Post-op Instructions" editor — the PURE half.
 * Plan: docs/plans/2026-10-15-post-op-auto-send-plan.md §2 decision 1, §5a.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE, with NAMED exports only (no `export default`).
 *    cypress/node-tests/post-op-editor.test.js executes this exact file (it strips `export ` and evals it),
 *    so what is pinned there is what ships. The components import it; the node-test cannot.
 *
 * THE ROW (stored in the TX_PRESENTER_SETTINGS_CONSENT blob, `mappings[]`, shared with Tx Presenter and Forms):
 *   { label?, codes:[String], formIDs:[formIDForWeb] | [], postOp?:{ formID, sms:'TRUE'|'FALSE', email:'TRUE'|'FALSE' },
 *     review?:{ consent?:'TRUE', postOp?:'TRUE' } }
 *   - `codes` are the value shape the code picker has always used, String(CodeNum || ProcCode).
 *   - Every flag in this feature is the 'TRUE'/'FALSE' STRING, read through `isOn` (a stray JSON true reads ON too).
 *   - `review` is written ONLY by the onboarding seeder (a seeded cell it could not match by name); the editor clears a
 *     flag the moment that cell is picked, and `cleanRowsForSave` strips it from any filled cell.
 *
 * ⛔ THE DROP RULE (decision 1) — keep a row iff it has a code AND (a consent form OR a post-op form). BOTH editors
 *    (this one and Tx Presenter's SettingsConsent) save through `cleanRowsForSave`, so a post-op-only row
 *    (`formIDs:[]` + postOp.formID) is never deleted by the classic editor, and label/postOp/review survive it.
 *
 * ⛔ A client-only `_key` may ride on working copies (stable v-for / menu identity). It is never saved and never
 *    counts as a change: `cleanRowsForSave` and `rowsSignature` both strip it.
 */

/** The tolerant boolean reader: the 'TRUE' string (what we write) or a JSON true (what a stray writer might). */
export function isOn(v) {
  return v === 'TRUE' || v === true;
}

function isPlainObject(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function nonBlank(v) {
  return typeof v === 'string' && v.trim() !== '';
}

/** The row's procedure codes as clean unique strings. */
export function rowCodes(row) {
  const list = row && Array.isArray(row.codes) ? row.codes : [];
  const out = [];
  list.forEach((c) => {
    if (c === null || c === undefined) return;
    const s = String(c).trim();
    if (s && out.indexOf(s) === -1) out.push(s);
  });
  return out;
}

export function rowHasCodes(row) {
  return rowCodes(row).length > 0;
}

/** The consent form id on a row ('' when none). `formIDs` is a one-element array by the Phase A decision. */
export function consentFormID(row) {
  const ids = row && Array.isArray(row.formIDs) ? row.formIDs : [];
  const found = ids.find(nonBlank);
  return found ? found : '';
}

/** The post-op form id on a row ('' when none). */
export function postOpFormID(row) {
  return row && isPlainObject(row.postOp) && nonBlank(row.postOp.formID) ? row.postOp.formID : '';
}

export function rowHasForm(row) {
  return !!(consentFormID(row) || postOpFormID(row));
}

/** A row the editor will keep on save (and the classic Consent editor will too): codes AND at least one form. */
export function isKeepableRow(row) {
  return isPlainObject(row) && rowHasCodes(row) && rowHasForm(row);
}

/** "Post-op only" — a post-op form and NO consent form. The classic Consent editor shows these greyed. */
export function isPostOpOnly(row) {
  return !!postOpFormID(row) && !consentFormID(row);
}

/** Does the cell still carry the seeder's "pick a form" flag while it is blank? kind: 'consent' | 'postOp'. */
export function cellNeedsReview(row, kind) {
  const review = row && isPlainObject(row.review) ? row.review : {};
  if (kind === 'postOp') return isOn(review.postOp) && !postOpFormID(row);
  return isOn(review.consent) && !consentFormID(row);
}

export function rowNeedsReview(row) {
  return cellNeedsReview(row, 'consent') || cellNeedsReview(row, 'postOp');
}

/**
 * Rows → the rows to PERSIST. Pure (inputs are never mutated).
 *   - drops rows without a code, or without any form (the drop rule above)
 *   - keeps every other key on the row (label, anything a later version adds) — nothing is rebuilt from scratch
 *   - `formIDs` is always an array (the consent matcher requires one), `postOp` is normalised to
 *     { formID, sms:'TRUE'|'FALSE', email:'TRUE'|'FALSE' } or removed when it has no form
 *   - strips `review.consent` / `review.postOp` once that cell is filled, and the whole `review` when empty
 *   - strips the client-only `_key` and a blank `label`
 */
export function cleanRowsForSave(rows) {
  const out = [];
  (Array.isArray(rows) ? rows : []).forEach((raw) => {
    if (!isPlainObject(raw)) return;
    const row = Object.assign({}, raw);
    delete row._key;

    const codes = rowCodes(row);
    const consent = consentFormID(row);
    const post = postOpFormID(row);
    if (codes.length === 0 || (!consent && !post)) return;

    row.codes = codes;
    row.formIDs = (Array.isArray(row.formIDs) ? row.formIDs : []).filter(nonBlank);

    if (post) {
      row.postOp = Object.assign({}, row.postOp, {
        formID: post,
        sms: isOn(row.postOp.sms) ? 'TRUE' : 'FALSE',
        email: isOn(row.postOp.email) ? 'TRUE' : 'FALSE',
      });
    } else {
      delete row.postOp;
    }

    if (isPlainObject(row.review)) {
      const review = Object.assign({}, row.review);
      if (consent) delete review.consent;
      if (post) delete review.postOp;
      if (Object.keys(review).length > 0) row.review = review;
      else delete row.review;
    } else {
      delete row.review;
    }

    if (typeof row.label === 'string') {
      const label = row.label.trim();
      if (label) row.label = label;
      else delete row.label;
    }
    out.push(row);
  });
  return out;
}

/**
 * A comparable string for dirty-checking working rows (the client `_key` is ignored; an unsaved EMPTY row still
 * counts as a change, so adding a row lights the save bar like the design does).
 */
export function rowsSignature(rows) {
  return JSON.stringify((Array.isArray(rows) ? rows : []).map((r) => {
    if (!isPlainObject(r)) return r;
    const copy = Object.assign({}, r);
    delete copy._key;
    return copy;
  }));
}

/**
 * The line shown UNDER a row that will not survive Save (design: "Row notes"). null when the row is fine.
 *   no form and not flagged     → "This row is empty and will be removed when you save."
 *   no form but still flagged   → the seeder's blank cell: "Pick a consent or post-op form, or …"  (departure: the
 *                                 design shows nothing here, but Save WOULD remove it)
 *   forms but no code           → "Add at least one procedure code, or this row will be removed when you save."
 */
export function rowNote(row) {
  const hasForm = rowHasForm(row);
  if (!hasForm && !rowNeedsReview(row)) return 'This row is empty and will be removed when you save.';
  if (!hasForm) return 'Pick a consent or post-op form, or this row will be removed when you save.';
  if (!rowHasCodes(row)) return 'Add at least one procedure code, or this row will be removed when you save.';
  return null;
}

// ── Immutable edits (each returns a NEW row; the caller keeps `_key`) ────────────────────────────────────────────

function withoutReviewKey(review, key) {
  if (!isPlainObject(review)) return review;
  const next = Object.assign({}, review);
  delete next[key];
  return Object.keys(next).length > 0 ? next : undefined;
}

function setReview(row, key) {
  const next = Object.assign({}, row);
  const review = withoutReviewKey(row.review, key);
  if (review) next.review = review;
  else delete next.review;
  return next;
}

/** Pick (or clear, with '') the consent form. Picking — even "no form" — answers the seeder's flag. */
export function withConsentForm(row, formID) {
  const next = setReview(row, 'consent');
  next.formIDs = nonBlank(formID) ? [formID] : [];
  return next;
}

/**
 * Pick (or clear, with '') the post-op form. The FIRST pick ticks both Text and Email (decision 16); changing the
 * form keeps whatever the office ticked. Clearing removes the whole postOp object.
 */
export function withPostOpForm(row, formID) {
  const next = setReview(row, 'postOp');
  if (!nonBlank(formID)) {
    delete next.postOp;
    return next;
  }
  if (postOpFormID(row)) {
    next.postOp = Object.assign({}, row.postOp, { formID });
  } else {
    next.postOp = { formID, sms: 'TRUE', email: 'TRUE' };
  }
  return next;
}

/** Tick / untick Text ('sms') or Email ('email'). A no-op without a post-op form (the checkboxes are disabled then). */
export function withChannel(row, channel, on) {
  if (!postOpFormID(row)) return row;
  if (channel !== 'sms' && channel !== 'email') return row;
  const next = Object.assign({}, row);
  next.postOp = Object.assign({}, row.postOp);
  next.postOp[channel] = on ? 'TRUE' : 'FALSE';
  return next;
}

export function withCodes(row, codes) {
  return Object.assign({}, row, { codes: (Array.isArray(codes) ? codes : []).map(String) });
}

export function withLabel(row, label) {
  return Object.assign({}, row, { label: String(label === null || label === undefined ? '' : label) });
}

/** A blank row for "+ Add row". */
export function newRow() {
  return { label: '', codes: [], formIDs: [] };
}

// ── Codes ─────────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * customer.procCodes → picker options. `value` is String(CodeNum || ProcCode) — the shape rows have always stored
 * (SettingsConsent.vue); `code` is the CDT code shown in mono; `text` is what the search matches.
 */
export function buildCodeOptions(procCodes) {
  const list = Array.isArray(procCodes) ? procCodes : [];
  return list
    .filter((c) => c && (c.CodeNum || c.ProcCode))
    .map((c) => {
      const code = String(c.ProcCode || c.CodeNum);
      const desc = String(c.Descript || '').trim();
      return { value: String(c.CodeNum || c.ProcCode), code, desc, text: desc ? `${code} - ${desc}` : code };
    });
}

/**
 * Stored code values → chips. A stored value the catalog does not know (a different CodeNum, a ProcCode string, a
 * catalog still loading) is KEPT as a bare chip — never filtered away, or the next Save would silently delete it
 * (the same trap insurance-verification-proc-code-picker.test.js pins).
 */
export function chipsForCodes(values, options) {
  const byValue = {};
  const byCode = {};
  (Array.isArray(options) ? options : []).forEach((o) => {
    byValue[o.value] = o;
    if (!byCode[o.code.toLowerCase()]) byCode[o.code.toLowerCase()] = o;
  });
  return rowCodes({ codes: values }).map((v) => {
    const o = byValue[v] || byCode[v.toLowerCase()];
    if (!o) return { value: v, code: v, desc: '', text: v };
    return { value: v, code: o.code, desc: o.desc, text: o.text };
  });
}

/** The options a row's code menu offers: not already on the row, matching the search, capped for the DOM. */
export function filterCodeOptions(options, selectedValues, query, limit) {
  const taken = {};
  rowCodes({ codes: selectedValues }).forEach((v) => { taken[v] = true; });
  const q = String(query === null || query === undefined ? '' : query).trim().toLowerCase();
  const max = Number.isInteger(limit) && limit > 0 ? limit : 200;
  const all = (Array.isArray(options) ? options : []).filter((o) => !taken[o.value]
    && (q === '' || o.code.toLowerCase().indexOf(q) !== -1 || o.desc.toLowerCase().indexOf(q) !== -1));
  return { items: all.slice(0, max), total: all.length, truncated: all.length > max };
}

// ── Forms menus ───────────────────────────────────────────────────────────────────────────────────────────────────

/** A post-op form is, by name, anything containing "post" (decision 14). */
export function isPostOpName(name) {
  return /post/i.test(String(name === null || name === undefined ? '' : name));
}

function formName(f) {
  return String((f && f.formName) || '');
}

/**
 * The form dropdown's groups.
 *   kind 'postOp'  no search: heading "Post-op forms" (names matching /post/i), then — behind "Show all forms · N more" —
 *                  "All other forms". If NO form is named like a post-op, everything is listed under "All forms" at once.
 *   kind 'consent' no search: one list, non-post forms first.
 *   a search      : every form whose name contains the text (case-insensitive), the kind's preferred names first.
 * forms: [{ formIDForWeb, formName }].
 * @returns {{ groups:[{heading:string, forms:[]}], none:boolean, showAllLink:boolean, otherCount:number }}
 */
export function formMenuGroups(forms, kind, query, showAll) {
  const list = (Array.isArray(forms) ? forms : []).filter((f) => f && nonBlank(f.formIDForWeb));
  const postFirst = kind === 'postOp';
  const prefer = (f) => (postFirst ? isPostOpName(formName(f)) : !isPostOpName(formName(f)));
  const preferred = list.filter(prefer);
  const rest = list.filter((f) => !prefer(f));
  const q = String(query === null || query === undefined ? '' : query).trim().toLowerCase();

  if (q) {
    const hits = preferred.concat(rest).filter((f) => formName(f).toLowerCase().indexOf(q) !== -1);
    return { groups: [{ heading: '', forms: hits }], none: hits.length === 0, showAllLink: false, otherCount: 0 };
  }
  if (!postFirst) {
    return { groups: [{ heading: '', forms: preferred.concat(rest) }], none: list.length === 0, showAllLink: false, otherCount: 0 };
  }
  if (preferred.length === 0) {
    return { groups: [{ heading: 'All forms', forms: rest }], none: rest.length === 0, showAllLink: false, otherCount: 0 };
  }
  const groups = [{ heading: 'Post-op forms', forms: preferred }];
  if (showAll && rest.length > 0) groups.push({ heading: 'All other forms', forms: rest });
  return { groups, none: false, showAllLink: !showAll && rest.length > 0, otherCount: rest.length };
}

/** The form's name for an id; '' when the form is not in the list (deleted, or the list is still loading). */
export function formNameFor(forms, formID) {
  const f = (Array.isArray(forms) ? forms : []).find((x) => x && x.formIDForWeb === formID);
  return f ? formName(f) : '';
}

// ── Analysis (summary line, confirm dialog, double-send warning) ─────────────────────────────────────────────────

function andList(items) {
  if (items.length < 2) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** The names of the active "Appointment Completed" workflows that ALSO text/email this row's codes on a channel it sends. */
export function workflowsForRow(row, overlaps) {
  if (!postOpFormID(row)) return [];
  const channels = [];
  if (isOn(row.postOp.sms)) channels.push('sms');
  if (isOn(row.postOp.email)) channels.push('email');
  if (channels.length === 0) return [];
  const codes = {};
  rowCodes(row).forEach((c) => { codes[c.toLowerCase()] = true; });
  const names = [];
  (Array.isArray(overlaps) ? overlaps : []).forEach((o) => {
    if (!o || channels.indexOf(String(o.channel).toLowerCase()) === -1) return;
    const hit = [o.codeNum, o.procCode].some((k) => k !== null && k !== undefined && k !== '' && codes[String(k).trim().toLowerCase()]);
    const name = o.workflowName ? String(o.workflowName) : '';
    if (hit && name && names.indexOf(name) === -1) names.push(name);
  });
  return names;
}

/**
 * Everything the editor shows ABOUT the rows, in one pass.
 *   groups       rows that will be saved (code + at least one form)
 *   withConsent / withPostOp / toReview     the summary line
 *   sending      rows with a post-op form AND Text or Email ticked  (the confirm dialog's "N procedure groups")
 *   smsRows / emailRows                      the confirm dialog's Text / Email tiles
 *   overlapRows  [{ key, label, workflows }] sending rows a workflow also messages  (banner + the ⚠ on the row)
 * `labelForCode(value)` names a code in the banner when a row has no group label (default: the value itself).
 */
export function analyzeRows(rows, options) {
  const opts = options || {};
  const labelForCode = typeof opts.labelForCode === 'function' ? opts.labelForCode : (v) => v;
  const list = Array.isArray(rows) ? rows : [];
  const valid = list.filter(isKeepableRow);
  const sending = valid.filter((r) => postOpFormID(r) && (isOn(r.postOp.sms) || isOn(r.postOp.email)));

  const overlapRows = [];
  sending.forEach((r) => {
    const workflows = workflowsForRow(r, opts.overlaps);
    if (workflows.length === 0) return;
    const label = (typeof r.label === 'string' && r.label.trim()) || rowCodes(r).map(labelForCode).join(', ');
    overlapRows.push({ key: r._key, label, workflows });
  });

  const n = overlapRows.length;
  const workflowNames = [];
  overlapRows.forEach((o) => o.workflows.forEach((w) => { if (workflowNames.indexOf(w) === -1) workflowNames.push(w); }));

  return {
    groups: valid.length,
    withConsent: valid.filter((r) => consentFormID(r)).length,
    withPostOp: valid.filter((r) => postOpFormID(r)).length,
    toReview: list.filter(rowNeedsReview).length,
    sending: sending.length,
    smsRows: sending.filter((r) => isOn(r.postOp.sms)).length,
    emailRows: sending.filter((r) => isOn(r.postOp.email)).length,
    overlapRows,
    overlapHead: n === 0 ? '' : `${n} ${n === 1 ? 'group may' : 'groups may'} send twice:`,
    overlapBody: n === 0 ? '' : `${andList(overlapRows.map((o) => o.label))} ${n === 1 ? 'is' : 'are'} also sent by the workflow ${workflowNames.map((w) => `‘${w}’`).join(', ')} when the appointment is completed. Turn that workflow off or untick Text/Email here.`,
  };
}

/** "N groups · N with consent · N with post-op" (+ the review count is a separate amber chip). */
export function summaryParts(analysis) {
  const a = analysis || {};
  return {
    groups: `${a.groups || 0} ${a.groups === 1 ? 'group' : 'groups'}`,
    consent: `${a.withConsent || 0} with consent`,
    postOp: `${a.withPostOp || 0} with post-op`,
    review: `${a.toReview || 0} to review`,
    hasReview: (a.toReview || 0) > 0,
  };
}

/** The tooltip on a row's ⚠. */
export function overlapTooltip(workflows) {
  const names = (Array.isArray(workflows) ? workflows : []).map((w) => `‘${w}’`).join(', ');
  return `Also sent by the workflow ${names} when the appointment is completed.`;
}
